import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useSidebar } from '@cloudflare/kumo'
import {
  CalendarBlankIcon,
  DotsThreeIcon,
  HouseIcon,
  UserPlusIcon,
  WifiHighIcon,
} from '@phosphor-icons/react'
import { useRoles } from '../auth/useRoles'
import { useTranslation } from '../i18n'
import './mobile-tab-bar.css'

/**
 * True when the bottom tab bar replaces the hamburger: a coworker or operator
 * (never an admin) on a screen narrow enough that Kumo's Sidebar is the
 * mobile drawer. `isMobile` is Kumo's own breakpoint (max-width: 767px), so
 * the bar and the drawer always switch together. Must be called inside
 * `Sidebar.Provider`.
 */
export function useShowMobileTabBar(): boolean {
  const { isMobile } = useSidebar()
  const roles = useRoles()
  const coworkerOrOperator =
    !roles.includes('admin') && roles.some((r) => r === 'user' || r === 'operator')
  return isMobile && coworkerOrOperator
}

const TEXT_INPUT =
  'input:not([type=button],[type=checkbox],[type=radio],[type=submit],[type=reset],[type=file],[type=range],[type=color]),textarea,[contenteditable]:not([contenteditable=false])'

/**
 * Heuristic: the on-screen keyboard is open when a text field has focus and
 * the visual viewport is much shorter than the layout viewport. Both iOS
 * Safari and Android Chrome (resizes-visual by default) shrink only the
 * visual viewport, so the bar would otherwise sit above the keyboard and eat
 * into the little space left. Multiplying by `scale` keeps pinch-zoom from
 * counting. Listens only while `enabled` (the bar is shown).
 */
function useOnScreenKeyboardOpen(enabled: boolean): boolean {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const vv = window.visualViewport
    if (!enabled || !vv) {
      setOpen(false)
      return
    }
    const update = () => {
      const el = document.activeElement
      const typing = el instanceof HTMLElement && el.matches(TEXT_INPUT)
      setOpen(typing && window.innerHeight - vv.height * vv.scale > 150)
    }
    // Leaving a field closes the keyboard. Moving to another field fires
    // focusin in the same task, so React batches both and nothing flickers.
    const reset = () => setOpen(false)
    vv.addEventListener('resize', update)
    document.addEventListener('focusin', update)
    document.addEventListener('focusout', reset)
    return () => {
      vv.removeEventListener('resize', update)
      document.removeEventListener('focusin', update)
      document.removeEventListener('focusout', reset)
    }
  }, [enabled])
  return open
}

/**
 * Kumo's Sidebar doesn't close its mobile drawer on navigation, so picking a
 * destination in it would leave the drawer covering the new page. Render
 * inside `Sidebar.Provider`; applies to every role.
 */
export function CloseMobileDrawerOnNavigate() {
  const { pathname } = useLocation()
  const { isMobile, setOpenMobile } = useSidebar()
  useEffect(() => {
    if (isMobile) setOpenMobile(false)
    // Deliberately keyed on the path only: a route change closes the drawer,
    // the drawer opening must not.
  }, [pathname])
  return null
}

interface Props {
  /** id of the Sidebar element the More button opens (for aria-controls). */
  drawerId: string
}

/**
 * Bottom tab bar for coworkers and operators on phones: Home, Bookings,
 * Guests, Wi-Fi, and More (opens the existing sidebar drawer, which still
 * holds every other destination). Renders nothing for admins and on wider
 * screens. Styles in mobile-tab-bar.css (Kumo tokens only).
 */
export function MobileTabBar({ drawerId }: Props) {
  const show = useShowMobileTabBar()
  const { openMobile, setOpenMobile } = useSidebar()
  const keyboardOpen = useOnScreenKeyboardOpen(show)
  const { t } = useTranslation()

  if (!show) return null

  const tabs = [
    { to: '/', label: t('nav.home'), icon: HouseIcon, end: true },
    { to: '/my/bookings', label: t('nav.bookings'), icon: CalendarBlankIcon, end: false },
    { to: '/my/guests', label: t('nav.guestInvitations'), icon: UserPlusIcon, end: false },
    { to: '/my/wifi', label: t('nav.wifi'), icon: WifiHighIcon, end: false },
  ]

  return (
    <nav
      className="mobile-tab-bar"
      aria-label={t('nav.tabBar')}
      data-testid="mobile-tab-bar"
      data-keyboard-open={keyboardOpen || undefined}
    >
      {tabs.map(({ to, label, icon: Icon, end }) => (
        // NavLink sets aria-current="page" itself; `end` makes Home exact
        // while the others also match nested routes (/my/bookings/new …).
        <NavLink key={to} to={to} end={end} className="mobile-tab-bar__item">
          {({ isActive }) => (
            <>
              <Icon size={24} weight={isActive ? 'fill' : 'regular'} aria-hidden />
              <span className="mobile-tab-bar__label">{label}</span>
            </>
          )}
        </NavLink>
      ))}
      <button
        type="button"
        className="mobile-tab-bar__item"
        aria-expanded={openMobile}
        aria-controls={drawerId}
        // Only ever opens: while the drawer is open its backdrop covers the
        // bar, and a tap there (or Esc, or focus leaving it) closes it.
        onClick={() => setOpenMobile(true)}
      >
        <DotsThreeIcon size={24} weight="bold" aria-hidden />
        <span className="mobile-tab-bar__label">{t('nav.more')}</span>
      </button>
    </nav>
  )
}
